import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { BarChart3, Download, ArrowUpDown, Search } from 'lucide-react';
import { format, subDays, startOfMonth, startOfYear } from 'date-fns';
import { fmtInt, fmtPct } from '../utils/format';

const PRESETS = [
  { label: 'Yesterday', key: 'yesterday' },
  { label: 'Last 7 Days', key: '7d' },
  { label: 'MTD', key: 'mtd' },
  { label: 'YTD', key: 'ytd' },
  { label: 'Custom', key: 'custom' },
];

function getPresetRange(key) {
  const today = new Date();
  switch (key) {
    case 'yesterday': return { start: format(subDays(today, 1), 'yyyy-MM-dd'), end: format(subDays(today, 1), 'yyyy-MM-dd') };
    case '7d': return { start: format(subDays(today, 7), 'yyyy-MM-dd'), end: format(subDays(today, 1), 'yyyy-MM-dd') };
    case 'mtd': return { start: format(startOfMonth(today), 'yyyy-MM-dd'), end: format(subDays(today, 1), 'yyyy-MM-dd') };
    case 'ytd': return { start: format(startOfYear(today), 'yyyy-MM-dd'), end: format(subDays(today, 1), 'yyyy-MM-dd') };
    default: return { start: format(subDays(today, 7), 'yyyy-MM-dd'), end: format(subDays(today, 1), 'yyyy-MM-dd') };
  }
}

// Persist filters across navigation so the user's selection survives tab
// switches and refreshes (matches the AdminInsights pattern).
const FILTER_STORAGE_KEY = 'restropulse:cost-insights:filters:v1';
const loadFilters = () => {
  try {
    const raw = window.localStorage.getItem(FILTER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

export default function CostInsights({ isAdmin = false }) {
  const { getHeaders, API } = useAuth();
  const initial = loadFilters() || {};
  const [outlets, setOutlets] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [portfolioId, setPortfolioId] = useState(initial.portfolioId || '');
  const [selectedOutlet, setSelectedOutlet] = useState(initial.selectedOutlet || '');
  const [preset, setPreset] = useState(initial.preset || '7d');
  const [customStart, setCustomStart] = useState(initial.customStart || '');
  const [customEnd, setCustomEnd] = useState(initial.customEnd || '');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortCol, setSortCol] = useState('date');
  const [sortDir, setSortDir] = useState('asc');

  // Persist filters whenever any change.
  useEffect(() => {
    try {
      window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({
        portfolioId, selectedOutlet, preset, customStart, customEnd,
      }));
    } catch { /* non-fatal */ }
  }, [portfolioId, selectedOutlet, preset, customStart, customEnd]);

  useEffect(() => {
    if (isAdmin) {
      (async () => {
        const [outletsRes, portfoliosRes] = await Promise.all([
          axios.get(`${API}/outlets`, { headers: getHeaders() }),
          axios.get(`${API}/admin/portfolios`, { headers: getHeaders() }).catch(() => ({ data: [] })),
        ]);
        setOutlets(outletsRes.data);
        setPortfolios(portfoliosRes.data || []);
        // Pick the first matching outlet if none stored, or if stored one doesn't exist
        const validIds = new Set(outletsRes.data.map(o => o.id));
        if (!initial.selectedOutlet || !validIds.has(initial.selectedOutlet)) {
          if (outletsRes.data.length > 0) setSelectedOutlet(outletsRes.data[0].id);
        }
      })();
    }
  // initial.selectedOutlet is stable (read once at mount); deps are fine.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, getHeaders, isAdmin]);

  // When portfolioId changes, ensure selectedOutlet still belongs to that portfolio
  // (otherwise default to the first outlet in the portfolio).
  useEffect(() => {
    if (!isAdmin || outlets.length === 0) return;
    const filtered = portfolioId
      ? outlets.filter(o => o.portfolio_id === portfolioId)
      : outlets;
    if (selectedOutlet && !filtered.some(o => o.id === selectedOutlet)) {
      setSelectedOutlet(filtered[0]?.id || '');
    }
  }, [portfolioId, outlets, selectedOutlet, isAdmin]);

  const load = useCallback(async () => {
    // Admin mode requires an outlet_id — wait until it's selected to avoid a 400 toast on first paint.
    if (isAdmin && !selectedOutlet) return;
    let start, end;
    if (preset === 'custom') {
      start = customStart;
      end = customEnd;
    } else {
      const r = getPresetRange(preset);
      start = r.start;
      end = r.end;
    }
    if (!start || !end) return;
    setLoading(true);
    try {
      const outletParam = isAdmin && selectedOutlet ? `&outlet_id=${selectedOutlet}` : '';
      const res = await axios.get(`${API}/insights/cost-table?start_date=${start}&end_date=${end}${outletParam}`, { headers: getHeaders() });
      setRows(res.data.rows || []);
    } catch { toast.error('Failed to load cost data'); }
    finally { setLoading(false); }
  }, [preset, customStart, customEnd, selectedOutlet, isAdmin, API, getHeaders]);

  useEffect(() => { load(); }, [load]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const sorted = [...rows].sort((a, b) => {
    const va = a[sortCol] ?? 0;
    const vb = b[sortCol] ?? 0;
    if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    return sortDir === 'asc' ? va - vb : vb - va;
  });

  const exportCSV = () => {
    const headers = ['Date', 'Kitchen Issues', 'Bar Issues', 'Store Issues', 'Kitchen Consumption', 'Bar Consumption', 'Food Cost %', 'Beverage Cost %', 'Other %'];
    const csvRows = [headers.join(',')];
    sorted.forEach(r => csvRows.push([r.date, r.kitchen_issues, r.bar_issues, r.store_issues, r.kitchen_consumption, r.bar_consumption, r.food_cost_pct, r.beverage_cost_pct, r.other_pct].join(',')));
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `cost_insights_${preset}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // Totals — skip null consumption values (Pending / Invalid days don't contribute)
  const totals = rows.reduce((acc, r) => ({
    kitchen_issues: acc.kitchen_issues + (r.kitchen_issues || 0),
    bar_issues: acc.bar_issues + (r.bar_issues || 0),
    store_issues: acc.store_issues + (r.store_issues || 0),
    kitchen_consumption: acc.kitchen_consumption + (r.kitchen_consumption || 0),
    bar_consumption: acc.bar_consumption + (r.bar_consumption || 0),
    food_sale: acc.food_sale + (r.food_sale || 0),
    beverage_sale: acc.beverage_sale + (r.beverage_sale || 0),
    net_sales: acc.net_sales + (r.net_sales || 0),
  }), { kitchen_issues: 0, bar_issues: 0, store_issues: 0, kitchen_consumption: 0, bar_consumption: 0, food_sale: 0, beverage_sale: 0, net_sales: 0 });

  const totalFoodPct = totals.food_sale > 0 ? totals.kitchen_consumption / totals.food_sale * 100 : null;
  const totalBevPct = totals.beverage_sale > 0 ? totals.bar_consumption / totals.beverage_sale * 100 : null;
  const totalOtherPct = totals.net_sales > 0 ? totals.store_issues / totals.net_sales * 100 : null;

  const SortHeader = ({ col, label }) => (
    <th className="px-3 py-2 text-right font-medium cursor-pointer hover:text-[#1A2421] select-none whitespace-nowrap"
      onClick={() => handleSort(col)}>
      <span className="inline-flex items-center gap-1">{label} <ArrowUpDown className="w-2.5 h-2.5 opacity-40" /></span>
    </th>
  );

  return (
    <div className="space-y-4" data-testid="cost-insights-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#1A2421]">Cost Insights</h1>
          <p className="text-sm text-[#5C6B64] mt-0.5">Food, Beverage, and Store cost tracking</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCSV} className="border-[#E2E8DF] text-[#5C6B64] text-xs" data-testid="export-csv">
          <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {isAdmin && portfolios.length > 0 && (
          <Select value={portfolioId || 'all'} onValueChange={(v) => setPortfolioId(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[180px] bg-white border-[#E2E8DF] text-sm" data-testid="portfolio-select">
              <SelectValue placeholder="Portfolio" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="portfolio-option-all">All Portfolios</SelectItem>
              {portfolios.map(p => (
                <SelectItem key={p.id} value={p.id} data-testid={`portfolio-option-${p.id}`}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {isAdmin && (
          <Select value={selectedOutlet} onValueChange={setSelectedOutlet}>
            <SelectTrigger className="w-[200px] bg-white border-[#E2E8DF] text-sm" data-testid="outlet-select"><SelectValue placeholder="Outlet" /></SelectTrigger>
            <SelectContent>
              {(portfolioId ? outlets.filter(o => o.portfolio_id === portfolioId) : outlets)
                .map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="flex gap-1">
          {PRESETS.map(p => (
            <button key={p.key} onClick={() => setPreset(p.key)}
              className={`text-[11px] px-2.5 py-1.5 rounded-md transition-colors ${
                preset === p.key ? 'bg-[color:var(--color-primary)] text-white' : 'bg-white border border-[#E2E8DF] text-[#5C6B64] hover:bg-[#F3F5F2]'
              }`} data-testid={`preset-${p.key}`}>
              {p.label}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <div className="flex items-center gap-2">
            <Input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
              className="w-[140px] h-8 text-sm bg-white border-[#E2E8DF]" data-testid="custom-start" />
            <span className="text-xs text-[#5C6B64]">to</span>
            <Input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              className="w-[140px] h-8 text-sm bg-white border-[#E2E8DF]" data-testid="custom-end" />
          </div>
        )}
      </div>

      <Card className="border-[#E2E8DF]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin w-6 h-6 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
            </div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-16 text-sm text-[#5C6B64]" data-testid="no-data">No cost data for this period</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cost-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#5C6B64] bg-[#F9F9F8] border-b border-[#E2E8DF]">
                    <th className="px-3 py-2 text-left font-medium cursor-pointer whitespace-nowrap" onClick={() => handleSort('date')}>
                      <span className="inline-flex items-center gap-1">Date <ArrowUpDown className="w-2.5 h-2.5 opacity-40" /></span>
                    </th>
                    <SortHeader col="kitchen_issues" label="Kitchen Issues" />
                    <SortHeader col="bar_issues" label="Bar Issues" />
                    <SortHeader col="store_issues" label="Store Issues" />
                    <SortHeader col="kitchen_consumption" label="Food Consumption" />
                    <SortHeader col="bar_consumption" label="Bev Consumption" />
                    <SortHeader col="food_cost_pct" label="Food %" />
                    <SortHeader col="beverage_cost_pct" label="Bev %" />
                    <SortHeader col="other_pct" label="Other %" />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r, i) => (
                    <tr key={r.date} className={`border-b border-[#E2E8DF]/60 ${i % 2 ? 'bg-[#F9F9F8]/30' : ''}`} data-testid={`cost-row-${r.date}`}>
                      <td className="px-3 py-2 font-medium text-[#1A2421] whitespace-nowrap">{r.date}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.kitchen_issues ? fmtInt(r.kitchen_issues) : '—'}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.bar_issues ? fmtInt(r.bar_issues) : '—'}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.store_issues ? fmtInt(r.store_issues) : '—'}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {r.kitchen_consumption_status === 'pending' ? <span className="text-[#D4A017] text-xs">Pending</span>
                          : r.kitchen_consumption_status === 'invalid' ? <span className="text-[#C45A41] text-xs">Invalid</span>
                          : r.kitchen_consumption ? fmtInt(r.kitchen_consumption) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {r.bar_consumption_status === 'pending' ? <span className="text-[#D4A017] text-xs">Pending</span>
                          : r.bar_consumption_status === 'invalid' ? <span className="text-[#C45A41] text-xs">Invalid</span>
                          : r.bar_consumption ? fmtInt(r.bar_consumption) : '—'}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono font-semibold ${r.food_cost_pct > 35 ? 'text-red-600' : r.food_cost_pct > 30 ? 'text-amber-600' : 'text-emerald-600'}`}>
                        {r.food_cost_pct > 0 ? `${r.food_cost_pct}%` : '—'}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono font-semibold ${r.beverage_cost_pct > 25 ? 'text-red-600' : r.beverage_cost_pct > 20 ? 'text-amber-600' : 'text-emerald-600'}`}>
                        {r.beverage_cost_pct > 0 ? `${r.beverage_cost_pct}%` : '—'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[#5C6B64]">{r.other_pct > 0 ? `${r.other_pct}%` : '—'}</td>
                    </tr>
                  ))}
                  {/* Totals row */}
                  <tr className="bg-[color:var(--color-primary)]/5 border-t-2 border-[color:var(--color-primary)]/20 font-semibold">
                    <td className="px-3 py-2 text-[color:var(--color-primary)]">Total</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtInt(totals.kitchen_issues)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtInt(totals.bar_issues)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtInt(totals.store_issues)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtInt(totals.kitchen_consumption)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtInt(totals.bar_consumption)}</td>
                    <td className="px-3 py-2 text-right font-mono">{totalFoodPct == null ? '—' : fmtPct(totalFoodPct)}</td>
                    <td className="px-3 py-2 text-right font-mono">{totalBevPct == null ? '—' : fmtPct(totalBevPct)}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#5C6B64]">{totalOtherPct == null ? '—' : fmtPct(totalOtherPct)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
